import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Button,
    Group,
    Modal,
    NumberInput,
    Paper,
    Select,
    Stack,
    Text,
    Textarea,
    TextInput,
    Title,
} from "@mantine/core";

import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";

import {
    IconAlertCircle,
    IconDeviceFloppy,
    IconPlus,
} from "@tabler/icons-react";

import {
    HotelBookingDto,
    HotelBookingUpdateDto,
    HotelDto,
} from "../../api/hotelBookingApi";

import {
    formatMoney,
    getNights,
    toYmd,
} from "../../utils/hotelBookingUtils";

import RoomBookingTable from "./RoomBookingTable";
import BookingSummary from "./BookingSummary";

import {
    AddRoomForm,
    BookingRoomRow,
    HotelBookingTexts,
} from "./HotelBookingTypes";

type Props = {
    opened: boolean;
    booking: HotelBookingDto | null;
    hotels: HotelDto[];
    saving: boolean;
    language: "en" | "da";
    t: HotelBookingTexts;

    onClose: () => void;

    onSave: (
        booking: HotelBookingDto,
        payload: HotelBookingUpdateDto
    ) => Promise<boolean>;
};

export default function EditBookingModal({
    opened,
    booking,
    hotels,
    saving,
    language,
    t,
    onClose,
    onSave,
}: Props) {
    const [country, setCountry] =
        useState("");

    const [contactName, setContactName] =
        useState("");

    const [contactEmail, setContactEmail] =
        useState("");

    const [
        whatsappCountryCode,
        setWhatsappCountryCode,
    ] = useState("");

    const [
        whatsappNumber,
        setWhatsappNumber,
    ] = useState("");

    const [comment, setComment] =
        useState("");

    const [rooms, setRooms] =
        useState<BookingRoomRow[]>([]);

    const [
        addRoomForm,
        setAddRoomForm,
    ] = useState<AddRoomForm>({
        hotelRoomTypeID: null,
        checkInDate: null,
        checkOutDate: null,
        guestsCount: 1,
    });

    const selectedHotel =
        useMemo(() => {
            if (!booking) return null;

            return (
                hotels.find(
                    (x) =>
                        x.hotelID ===
                        booking.hotelID
                ) ?? null
            );
        }, [booking, hotels]);

    const bookingStartDate =
        useMemo(
            () =>
                selectedHotel?.startBooking
                    ? new Date(
                        selectedHotel.startBooking
                    )
                    : null,
            [selectedHotel]
        );

    const bookingEndDate =
        useMemo(
            () =>
                selectedHotel?.endBooking
                    ? new Date(
                        selectedHotel.endBooking
                    )
                    : null,
            [selectedHotel]
        );

    const roomTypeSelectData =
        useMemo(() => {
            if (!selectedHotel) {
                return [];
            }

            return selectedHotel.roomTypes
                .filter(
                    (x) =>
                        x.isActive
                )
                .map(
                    (roomType) => ({
                        value:
                            String(
                                roomType.hotelRoomTypeID
                            ),

                        label:
                            `${roomType.name} � ${formatMoney(
                                roomType.pricePerNight
                            )} / night`,
                    })
                );
        }, [selectedHotel]);

    useEffect(() => {
        if (
            !opened ||
            !booking
        ) {
            return;
        }

        setCountry(
            booking.country ?? ""
        );

        setContactName(
            booking.contactName ?? ""
        );

        setContactEmail(
            booking.contactEmail ?? ""
        );

        setWhatsappCountryCode(
            booking.whatsappCountryCode ??
            ""
        );

        setWhatsappNumber(
            booking.whatsappNumber ?? ""
        );

        setComment(
            booking.comment ?? ""
        );

        const mappedRooms:
            BookingRoomRow[] =
            booking.rooms?.map(
                (room) => {
                    const roomType =
                        hotels
                            .find(
                                (h) =>
                                    h.hotelID ===
                                    booking.hotelID
                            )
                            ?.roomTypes.find(
                                (r) =>
                                    r.hotelRoomTypeID ===
                                    room.hotelRoomTypeID
                            );

                    const checkIn =
                        new Date(
                            room.checkInDate
                        );

                    const checkOut =
                        new Date(
                            room.checkOutDate
                        );

                    const nights =
                        getNights(
                            checkIn,
                            checkOut
                        );

                    const pricePerNight =
                        roomType?.pricePerNight ??
                        room.pricePerNight;

                    return {
                        id: String(
                            room.hotelBookingRoomID
                        ),

                        hotelRoomTypeID:
                            room.hotelRoomTypeID,

                        roomTypeName:
                            roomType?.name ??
                            room.roomTypeName ??
                            `Room ${room.hotelRoomTypeID}`,

                        checkInDate:
                            checkIn,

                        checkOutDate:
                            checkOut,

                        guestsCount:
                            room.guestsCount,

                        pricePerNight,

                        nights,

                        totalPrice:
                            nights *
                            pricePerNight,
                    };
                }
            ) ?? [];

        setRooms(
            mappedRooms
        );

        setAddRoomForm({
            hotelRoomTypeID: null,
            checkInDate:
                bookingStartDate,
            checkOutDate:
                bookingEndDate,
            guestsCount: 1,
        });
    }, [
        opened,
        booking,
        hotels,
        bookingStartDate,
        bookingEndDate,
    ]);

    function emailValid(
        value: string
    ) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
            value.trim()
        );
    }

    function isDateOutsideBookingWindow(
        date: Date
    ) {
        if (
            bookingStartDate &&
            date < bookingStartDate
        ) {
            return true;
        }

        if (
            bookingEndDate &&
            date > bookingEndDate
        ) {
            return true;
        }

        return false;
    }

    function updateRoom(
        id: string,
        changes: Partial<BookingRoomRow>
    ) {
        setRooms((current) =>
            current.map((room) => {
                if (
                    room.id !== id
                ) {
                    return room;
                }

                const updated = {
                    ...room,
                    ...changes,
                };

                /*
                 * Check-out skal altid v�re
                 * efter check-in.
                 */
                if (
                    updated.checkOutDate <=
                    updated.checkInDate
                ) {
                    notifications.show({
                        color: "red",
                        title: t.error,
                        message:
                            language === "en"
                                ? "Check-out must be after check-in."
                                : "Check-out skal v�re efter check-in.",
                    });

                    return room;
                }

                if (
                    bookingStartDate &&
                    updated.checkInDate <
                    bookingStartDate
                ) {
                    notifications.show({
                        color: "red",
                        title: t.error,
                        message:
                            language === "en"
                                ? "Check-in cannot be before the booking period."
                                : "Check-in kan ikke ligge f�r bookingperioden.",
                    });

                    return room;
                }

                if (
                    bookingEndDate &&
                    updated.checkOutDate >
                    bookingEndDate
                ) {
                    notifications.show({
                        color: "red",
                        title: t.error,
                        message:
                            language === "en"
                                ? "Check-out cannot be after the booking period."
                                : "Check-out kan ikke ligge efter bookingperioden.",
                    });

                    return room;
                }

                const roomType =
                    selectedHotel?.roomTypes.find(
                        (x) =>
                            x.hotelRoomTypeID ===
                            updated.hotelRoomTypeID
                    );

                if (
                    roomType &&
                    updated.guestsCount >
                    roomType.capacity
                ) {
                    notifications.show({
                        color: "red",
                        title: t.error,
                        message:
                            language === "en"
                                ? `${roomType.name} allows maximum ${roomType.capacity} guests.`
                                : `${roomType.name} tillader maks. ${roomType.capacity} g�ster.`,
                    });

                    return room;
                }

                const nights =
                    getNights(
                        updated.checkInDate,
                        updated.checkOutDate
                    );

                return {
                    ...updated,
                    nights,
                    totalPrice:
                        nights *
                        updated.pricePerNight,
                };
            })
        );
    }

    function addRoom() {
        if (
            !selectedHotel
        ) {
            return;
        }

        if (
            !addRoomForm.hotelRoomTypeID
        ) {
            notifications.show({
                color: "red",
                title: t.missing,
                message:
                    language === "en"
                        ? "Choose a room type."
                        : "V�lg en v�relsestype.",
            });

            return;
        }

        if (
            !addRoomForm.checkInDate ||
            !addRoomForm.checkOutDate
        ) {
            notifications.show({
                color: "red",
                title: t.missing,
                message:
                    language === "en"
                        ? "Choose check-in and check-out."
                        : "V�lg check-in og check-out.",
            });

            return;
        }

        if (
            addRoomForm.checkOutDate <=
            addRoomForm.checkInDate
        ) {
            notifications.show({
                color: "red",
                title: t.error,
                message:
                    language === "en"
                        ? "Check-out must be after check-in."
                        : "Check-out skal v�re efter check-in.",
            });

            return;
        }

        if (
            bookingStartDate &&
            addRoomForm.checkInDate <
            bookingStartDate
        ) {
            notifications.show({
                color: "red",
                title: t.error,
                message:
                    language === "en"
                        ? "Check-in is before the hotel booking period."
                        : "Check-in ligger f�r hotellets bookingperiode.",
            });

            return;
        }

        if (
            bookingEndDate &&
            addRoomForm.checkOutDate >
            bookingEndDate
        ) {
            notifications.show({
                color: "red",
                title: t.error,
                message:
                    language === "en"
                        ? "Check-out is after the hotel booking period."
                        : "Check-out ligger efter hotellets bookingperiode.",
            });

            return;
        }

        const roomType =
            selectedHotel.roomTypes.find(
                (x) =>
                    x.hotelRoomTypeID ===
                    addRoomForm.hotelRoomTypeID
            );

        if (!roomType) {
            notifications.show({
                color: "red",
                title: t.error,
                message:
                    language === "en"
                        ? "Room type not found."
                        : "V�relsestypen blev ikke fundet.",
            });

            return;
        }

        if (
            addRoomForm.guestsCount >
            roomType.capacity
        ) {
            notifications.show({
                color: "red",
                title: t.error,
                message:
                    language === "en"
                        ? `${roomType.name} allows maximum ${roomType.capacity} guests.`
                        : `${roomType.name} tillader maks. ${roomType.capacity} g�ster.`,
            });

            return;
        }

        const nights =
            getNights(
                addRoomForm.checkInDate,
                addRoomForm.checkOutDate
            );

        setRooms(
            (current) => [
                ...current,
                {
                    id:
                        crypto.randomUUID(),

                    hotelRoomTypeID:
                        roomType.hotelRoomTypeID,

                    roomTypeName:
                        roomType.name,

                    checkInDate:
                        addRoomForm.checkInDate!,

                    checkOutDate:
                        addRoomForm.checkOutDate!,

                    guestsCount:
                        addRoomForm.guestsCount,

                    pricePerNight:
                        roomType.pricePerNight,

                    nights,

                    totalPrice:
                        nights *
                        roomType.pricePerNight,
                },
            ]
        );

        setAddRoomForm(
            (current) => ({
                ...current,
                hotelRoomTypeID:
                    null,
                guestsCount: 1,
            })
        );
    }

    function removeRoom(
        id: string
    ) {
        setRooms(
            (current) =>
                current.filter(
                    (x) =>
                        x.id !== id
                )
        );
    }

    async function save() {
        if (!booking) {
            return;
        }

        if (
            !country.trim()
        ) {
            notifications.show({
                color: "red",
                title: t.missing,
                message:
                    language === "en"
                        ? "Country is required."
                        : "Land skal udfyldes.",
            });

            return;
        }

        if (
            !contactName.trim()
        ) {
            notifications.show({
                color: "red",
                title: t.missing,
                message:
                    language === "en"
                        ? "Contact person is required."
                        : "Kontaktperson skal udfyldes.",
            });

            return;
        }

        if (
            !emailValid(
                contactEmail
            )
        ) {
            notifications.show({
                color: "red",
                title: t.missing,
                message:
                    language === "en"
                        ? "Enter a valid email address."
                        : "Indtast en gyldig emailadresse.",
            });

            return;
        }

        if (
            rooms.length === 0
        ) {
            notifications.show({
                color: "red",
                title: t.missing,
                message:
                    language === "en"
                        ? "The booking must contain at least one room."
                        : "Bookingen skal indeholde mindst �t v�relse.",
            });

            return;
        }

        const success =
            await onSave(
                booking,
                {
                    country:
                        country.trim(),

                    contactName:
                        contactName.trim(),

                    contactEmail:
                        contactEmail.trim(),

                    whatsappCountryCode:
                        whatsappCountryCode.trim() ||
                        null,

                    whatsappNumber:
                        whatsappNumber.trim() ||
                        null,

                    comment:
                        comment.trim() ||
                        null,

                    rooms:
                        rooms.map(
                            (room) => ({
                                hotelRoomTypeID:
                                    room.hotelRoomTypeID,

                                checkInDate:
                                    toYmd(
                                        room.checkInDate
                                    ),

                                checkOutDate:
                                    toYmd(
                                        room.checkOutDate
                                    ),

                                guestsCount:
                                    room.guestsCount,
                            })
                        ),
                }
            );

        if (success) {
            onClose();
        }
    }

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={
                booking
                    ? language ===
                        "en"
                        ? `Edit booking #${booking.hotelBookingID}`
                        : `Ret booking #${booking.hotelBookingID}`
                    : ""
            }
            size="90%"
            centered
            styles={{
                content: {
                    maxWidth: 1050,
                },
            }}
        >
            {booking && (
                <Stack gap="lg">
                    <Alert
                        color="blue"
                        variant="light"
                        icon={
                            <IconAlertCircle
                                size={18}
                            />
                        }
                    >
                        {language ===
                            "en"
                            ? "You can change the booking until payment has been completed."
                            : "Bookingen kan rettes, indtil betalingen er gennemf�rt."}
                    </Alert>

                    {booking.status
                        .toLowerCase()
                        .replace(
                            /\s+/g,
                            ""
                        ) ===
                        "awaitingpayment" && (
                            <Alert
                                color="yellow"
                                variant="light"
                            >
                                {language ===
                                    "en"
                                    ? "The existing payment link will be replaced when the booking is changed."
                                    : "Det eksisterende betalingslink bliver erstattet, n�r bookingen �ndres."}
                            </Alert>
                        )}

                    <Paper
                        withBorder
                        p="md"
                        radius="md"
                    >
                        <Stack gap="sm">
                            <Title order={4}>
                                {t.hotel}
                            </Title>

                            <Text fw={700}>
                                {booking.hotelName ??
                                    selectedHotel?.name ??
                                    "-"}
                            </Text>

                            <Text
                                size="sm"
                                c="dimmed"
                            >
                                {language ===
                                    "en"
                                    ? "The hotel itself cannot be changed on an existing booking."
                                    : "Selve hotellet kan ikke �ndres p� en eksisterende booking."}
                            </Text>
                        </Stack>
                    </Paper>

                    <Paper
                        withBorder
                        p="md"
                        radius="md"
                    >
                        <Stack gap="md">
                            <Title order={4}>
                                {
                                    t.contactInfo
                                }
                            </Title>

                            <Group grow>
                                <TextInput
                                    label={
                                        t.country
                                    }
                                    value={
                                        country
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setCountry(
                                            e
                                                .currentTarget
                                                .value
                                        )
                                    }
                                    required
                                />

                                <TextInput
                                    label={
                                        t.contactPerson
                                    }
                                    value={
                                        contactName
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setContactName(
                                            e
                                                .currentTarget
                                                .value
                                        )
                                    }
                                    required
                                />
                            </Group>

                            <TextInput
                                label={
                                    t.contactEmail
                                }
                                value={
                                    contactEmail
                                }
                                onChange={(
                                    e
                                ) =>
                                    setContactEmail(
                                        e
                                            .currentTarget
                                            .value
                                    )
                                }
                                type="email"
                                required
                            />

                            <Group grow>
                                <TextInput
                                    label={
                                        language ===
                                            "en"
                                            ? "WhatsApp country code"
                                            : "WhatsApp landekode"
                                    }
                                    placeholder="+45"
                                    value={
                                        whatsappCountryCode
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setWhatsappCountryCode(
                                            e
                                                .currentTarget
                                                .value
                                        )
                                    }
                                />

                                <TextInput
                                    label={
                                        language ===
                                            "en"
                                            ? "WhatsApp number"
                                            : "WhatsApp nummer"
                                    }
                                    value={
                                        whatsappNumber
                                    }
                                    onChange={(
                                        e
                                    ) =>
                                        setWhatsappNumber(
                                            e
                                                .currentTarget
                                                .value
                                        )
                                    }
                                />
                            </Group>
                        </Stack>
                    </Paper>

                    <Paper
                        withBorder
                        p="md"
                        radius="md"
                    >
                        <Stack gap="md">
                            <Title order={4}>
                                {language ===
                                    "en"
                                    ? "Rooms"
                                    : "V�relser"}
                            </Title>

                            <Group
                                grow
                                align="flex-end"
                            >
                                <Select
                                    label={
                                        t.roomType
                                    }
                                    placeholder={
                                        t.chooseType
                                    }
                                    data={
                                        roomTypeSelectData
                                    }
                                    value={
                                        addRoomForm.hotelRoomTypeID
                                            ? String(
                                                addRoomForm.hotelRoomTypeID
                                            )
                                            : null
                                    }
                                    onChange={(
                                        value
                                    ) =>
                                        setAddRoomForm(
                                            (
                                                current
                                            ) => ({
                                                ...current,

                                                hotelRoomTypeID:
                                                    value
                                                        ? Number(
                                                            value
                                                        )
                                                        : null,
                                            })
                                        )
                                    }
                                    searchable
                                />

                                <DateInput
                                    label={
                                        t.checkIn
                                    }
                                    value={
                                        addRoomForm.checkInDate
                                    }
                                    onChange={(
                                        value
                                    ) =>
                                        setAddRoomForm(
                                            (
                                                current
                                            ) => ({
                                                ...current,

                                                checkInDate:
                                                    value,
                                            })
                                        )
                                    }
                                    minDate={
                                        bookingStartDate ??
                                        undefined
                                    }
                                    maxDate={
                                        bookingEndDate ??
                                        undefined
                                    }
                                    excludeDate={
                                        isDateOutsideBookingWindow
                                    }
                                />

                                <DateInput
                                    label={
                                        t.checkOut
                                    }
                                    value={
                                        addRoomForm.checkOutDate
                                    }
                                    onChange={(
                                        value
                                    ) =>
                                        setAddRoomForm(
                                            (
                                                current
                                            ) => ({
                                                ...current,

                                                checkOutDate:
                                                    value,
                                            })
                                        )
                                    }
                                    minDate={
                                        bookingStartDate ??
                                        undefined
                                    }
                                    maxDate={
                                        bookingEndDate ??
                                        undefined
                                    }
                                    excludeDate={
                                        isDateOutsideBookingWindow
                                    }
                                />

                                <NumberInput
                                    label={
                                        t.guests
                                    }
                                    min={1}
                                    max={10}
                                    value={
                                        addRoomForm.guestsCount
                                    }
                                    onChange={(
                                        value
                                    ) =>
                                        setAddRoomForm(
                                            (
                                                current
                                            ) => ({
                                                ...current,

                                                guestsCount:
                                                    Number(
                                                        value ||
                                                        1
                                                    ),
                                            })
                                        )
                                    }
                                />

                                <Button
                                    leftSection={
                                        <IconPlus
                                            size={
                                                16
                                            }
                                        />
                                    }
                                    onClick={
                                        addRoom
                                    }
                                >
                                    {
                                        t.addToList
                                    }
                                </Button>
                            </Group>

                            <RoomBookingTable
                                rooms={rooms}
                                onRemoveRoom={
                                    removeRoom
                                }
                                onUpdateRoom={
                                    updateRoom
                                }
                                editable
                                minDate={
                                    bookingStartDate
                                }
                                maxDate={
                                    bookingEndDate
                                }
                                t={t}
                            />
                        </Stack>
                    </Paper>

                    <BookingSummary
                        rooms={rooms}
                        t={t}
                    />

                    <Textarea
                        label={
                            t.comments
                        }
                        value={
                            comment
                        }
                        onChange={(e) =>
                            setComment(
                                e
                                    .currentTarget
                                    .value
                            )
                        }
                        minRows={3}
                    />

                    <Group justify="space-between">
                        <Button
                            variant="default"
                            onClick={
                                onClose
                            }
                            disabled={
                                saving
                            }
                        >
                            {language ===
                                "en"
                                ? "Cancel"
                                : "Annuller"}
                        </Button>

                        <Button
                            color="green"
                            loading={
                                saving
                            }
                            leftSection={
                                <IconDeviceFloppy
                                    size={
                                        17
                                    }
                                />
                            }
                            onClick={
                                save
                            }
                        >
                            {language ===
                                "en"
                                ? "Save changes"
                                : "Gem �ndringer"}
                        </Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
}